import React from 'react';
import { FiArrowLeft, FiArrowRight, FiArrowUpRight } from 'react-icons/fi';
import { Link, useParams } from 'react-router-dom';
import { getPortfolioProject, portfolioArchive, type PortfolioProject } from '../data/portfolio';
import PortfolioMedia from './PortfolioMedia';

const categoryOf = (project: PortfolioProject) => {
  if (project.categories.includes('콘텐츠 디자인')) return 'CONTENT';
  if (project.categories.includes('앱')) return 'APPLICATION';
  return 'WEBSITE';
};

const PortfolioDetail: React.FC = () => {
  const { slug } = useParams();
  const project = slug ? getPortfolioProject(slug) : undefined;

  if (!project) {
    return (
      <main className="sp-page sp-missing">
        <div className="studio-shell">
          <p className="sp-section-label">PROJECT ARCHIVE / 404</p>
          <h1>프로젝트를 찾을 수 없습니다<span>.</span></h1>
          <Link to="/portfolio">전체 프로젝트 보기 <FiArrowRight /></Link>
        </div>
      </main>
    );
  }

  const relatedProjects = portfolioArchive
    .filter((item) => item.slug !== project.slug && item.categories.some((category) => project.categories.includes(category)))
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 3);

  return (
    <main className="sp-page">
      <section className="sp-detail-hero" aria-labelledby="sp-detail-title">
        <div className="studio-shell">
          <div className="sp-ruleline"><span>PROJECT ARCHIVE / {categoryOf(project)}</span><span>{project.date.slice(0, 4)}</span></div>
          <Link className="sp-backlink" to="/portfolio"><FiArrowLeft /> 전체 프로젝트</Link>
          <div className="sp-detail-hero-grid">
            <div className="sp-detail-hero-copy">
              <p className="sp-eyebrow">{categoryOf(project)} · {project.date.slice(0, 4)}</p>
              <h1 id="sp-detail-title">{project.title}<span>.</span></h1>
              <p className="sp-detail-subtitle">{project.subtitle}</p>
              <p className="sp-detail-description">{project.description}</p>
              <span className="sp-detail-scroll">SCROLL TO EXPLORE ↓</span>
            </div>
            <div className="sp-detail-media-frame">
              <div className="sp-detail-media-top"><span>SELECTED WORK</span><span>SOMNIUM LIVE / {project.date.slice(0, 4)}</span></div>
              <div className="sp-detail-media"><PortfolioMedia project={project} className="sp-detail-media-image" /></div>
              <div className="sp-detail-media-bottom"><span>{project.scope}</span><span>↗</span></div>
            </div>
          </div>
        </div>
      </section>

      <section className="sp-detail-facts" aria-label="프로젝트 정보">
        <div className="studio-shell sp-detail-facts-grid">
          <div><span>01 / PROJECT</span><strong>{project.title}</strong></div>
          <div><span>02 / CLIENT</span><strong>{project.client}</strong></div>
          <div><span>03 / YEAR</span><strong>{project.date.slice(0, 4)}</strong></div>
          <div><span>04 / SCOPE</span><strong>{project.scope}</strong></div>
        </div>
      </section>

      <section className="sp-detail-content">
        <div className="studio-shell">
          <div className="sp-detail-section sp-detail-section-first">
            <div className="sp-detail-section-heading"><p className="sp-section-label">01 / THE BRIEF</p><h2>어떤 일을<br/>해결했나요<span>.</span></h2></div>
            <p className="sp-detail-section-copy">{project.background}</p>
          </div>

          {project.tasks.length > 0 && (
            <section className="sp-detail-tasks" aria-labelledby="sp-tasks-title">
              <div className="sp-detail-section-heading"><p className="sp-section-label">02 / WHAT WE DID</p><h2 id="sp-tasks-title">실제로 한 일<span>.</span></h2></div>
              <ol>
                {project.tasks.map((task, index) => <li key={`${index}-${task}`}><span>{String(index + 1).padStart(2, '0')}</span><strong>{task}</strong></li>)}
              </ol>
            </section>
          )}

          {project.screens && project.screens.length > 0 && (
            <section className="sp-detail-screens" aria-labelledby="sp-screens-title">
              <div className="sp-detail-section-heading"><p className="sp-section-label">SELECTED SCREENS</p><h2 id="sp-screens-title">프로젝트 화면<span>.</span></h2></div>
              <div className="sp-detail-screen-grid">
                {project.screens.map((screen, index) => <figure key={screen}><img src={screen} alt={`${project.title} 화면 ${index + 1}`} loading="lazy" /><figcaption>{String(index + 1).padStart(2, '0')} / {project.title}</figcaption></figure>)}
              </div>
            </section>
          )}

          <section className="sp-detail-result" aria-labelledby="sp-result-title">
            <div className="sp-detail-section-heading"><p className="sp-section-label">03 / THE RESULT</p><h2 id="sp-result-title">남긴 결과<span>.</span></h2></div>
            <div><p>{project.outcome}</p>{project.tools && project.tools.length > 0 && <div className="sp-detail-tags" aria-label="사용 기술 및 도구">{project.tools.map((tool) => <span key={tool}>{tool}</span>)}</div>}</div>
          </section>
        </div>
      </section>

      {relatedProjects.length > 0 && (
        <section className="sp-related" aria-labelledby="sp-related-title">
          <div className="studio-shell">
            <div className="sp-related-head"><div><p className="sp-section-label">RELATED WORK</p><h2 id="sp-related-title">이런 작업도 했습니다<span>.</span></h2></div><Link to="/portfolio">전체 보기 <FiArrowRight /></Link></div>
            <div className="sp-related-list">
              {relatedProjects.map((item, index) => <Link key={item.slug} to={`/portfolio/${item.slug}`}><span>{String(index + 1).padStart(2, '0')}</span><strong>{item.title}</strong><small>{categoryOf(item)} · {item.date.slice(0, 4)}</small><FiArrowUpRight /></Link>)}
            </div>
          </div>
        </section>
      )}

      <section className="sp-bottom-cta">
        <div className="studio-shell sp-bottom-cta-inner">
          <div><p className="sp-section-label">NEXT PROJECT</p><h2>다음 프로젝트를<br/>함께 만들까요<span>?</span></h2></div>
          <a href="/#contact">제작 상담하기 <FiArrowRight /></a>
        </div>
      </section>
    </main>
  );
};

export default PortfolioDetail;
